import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ESCALATION_TYPES, escalateTicket } from '../api/easeedesk'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'

/**
 * Fereastra "Escalate ticket" de pe filele Contacts si Fast Dispatch -
 * `#fastDispatchEscalateModal` din `tickets/details/modals.php`, cu scriptul
 * din `fast_dispatch.js`: butonul de pe om deschide fereastra cu tichetul,
 * grupul si omul in campuri ascunse, se scrie un comentariu, si "Escalate"
 * trimite la `easeedesk/escalateTicket` cu `escalation_type = 1`. Reusita:
 * "Ticket escalated." (toastr), fereastra se inchide, formularul se goleste.
 *
 * Capul e cel deja mutat, `POST tickets/{id}/escalate/1` - aceeasi scriere ca
 * butonul "Escalate" din fila Change, doar ca tinta e aleasa de pe om.
 */

export interface EscalateTarget {
  user: number
  group: number
  name: string
}

export function TicketEscalateModal({ ticket, target, onClose }: { ticket: number; target: EscalateTarget | null; onClose(): void }) {
  const client = useQueryClient()
  const [comments, setComments] = useState('')
  const [busy, setBusy] = useState(false)

  const close = () => {
    setComments('')
    onClose()
  }

  const submit = () => {
    if (!target) return
    setBusy(true)
    escalateTicket(ticket, ESCALATION_TYPES.internal, { group: target.group, user: target.user, comments })
      .then(() => {
        toast('Ticket escalated.', 'Success', 'success')
        void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticket] })
        void client.invalidateQueries({ queryKey: ['easeedesk', 'table'] })
        close()
      })
      .catch((err: unknown) => {
        toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
        close()
      })
      .finally(() => setBusy(false))
  }

  return (
    <Modal id="fastDispatchEscalateModal" open={target !== null} onClose={close} title="Escalate ticket" labelledBy="fastDispatchEscalateModalLabel">
      <form id="fastDispatchEscalateForm" onSubmit={(ev) => ev.preventDefault()}>
        <div className="form-group">
          <input type="hidden" id="escalateTicketID" name="ticket_id" value={ticket} readOnly />
          <input type="hidden" name="escalation_type" value="1" readOnly />
          <input type="hidden" id="escalateGroup" name="group" value={target?.group ?? ''} readOnly />
          <input type="hidden" id="escalateUser" name="user" value={target?.user ?? ''} readOnly />
          <label htmlFor="escalateComment" className="control-label">
            Comment
          </label>
          <textarea className="form-control" id="escalateComment" name="comments" value={comments} onChange={(ev) => setComments(ev.target.value)}></textarea>
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={close}>
            Close
          </button>
          <button type="button" className="btn btn-primary submitEscalateTicket" disabled={busy} onClick={submit}>
            Escalate
          </button>
        </div>
      </form>
    </Modal>
  )
}

/** Butonul galben de pe om - `.fast_dispatch_escalate`. */
export function EscalateButton({ ticket, person, onPick }: { ticket: number; person: EscalateTarget; onPick(target: EscalateTarget): void }) {
  return (
    <button
      type="button"
      className="btn btn-warning btn-xs fast_dispatch_escalate pull-right"
      data-ticket={ticket}
      data-user={person.user}
      data-group={person.group}
      data-username={person.name}
      onClick={() => onPick(person)}
    >
      <i className="fas fa-external-link-alt"></i> Escalate
    </button>
  )
}
